// The organisation's owner, as head office follows it (spec/14 §3.1, F-HQ-02, ADR-0046):
// `GET /v1/hq/organizations/{o}/owner` says whether the owner has joined — who, and when — or what became
// of the newest owner invitation: invited (when, by whom, by email or phone, and whether its message
// went), expired, or withdrawn. Its own component, so the organisation's page carries one line for it.
//
//   invited     Send a fresh invitation (a new link to the same contact, the old one withdrawn), Copy the
//               link (read from the api, recorded in the organisation's log, for when the message did not
//               go or the owner asks), Withdraw (confirmed in a centred dialog first).
//   expired,    Send a fresh invitation to the same contact, or Invite someone else (a centred dialog).
//   withdrawn
//   none        Invite an owner (the same dialog).
//   joined      Nothing to do here: from now on the organisation's owners invite owners.
//
// Reading it writes no audit row — the owner invitation is head office's own onboarding record — and
// every change is audited in the organisation's log by the api. The api refuses an invitation once an
// owner has joined (`409`), and the panel then reads the owner again.

import type { CreatedInvitation, HqOrganizationOwner, HqOwnerInvitation } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, type BadgeTone, Button, Dialog, InfoTip, PersonCard, Skeleton, useButtonFeedback, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { problemCode, PROBLEM } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { usePhoneSignIn } from '../../auth/sign-in-methods';
import { LoadFailure } from '../../shell/LoadFailure';
import { EMPTY_OWNER, InvitationOutcome, ownerContactOf, OwnerContactFields, placeFieldErrors, type OwnerContactValues } from './owner';
import {
  useInviteOwner,
  useLastInvitation,
  useOrganizationOwner,
  useOwnerInvitationLink,
  useResendOwnerInvitation,
  useWithdrawOwnerInvitation,
} from './queries';

const STATUS_TONE: Readonly<Record<HqOrganizationOwner['status'], BadgeTone>> = {
  joined: 'ok',
  invited: 'info',
  expired: 'warn',
  withdrawn: 'neutral',
  none: 'neutral',
};

/** The owner's status as a badge: the list's column and this panel say it the same way. */
export function OwnerStatusBadge({ status }: { readonly status: HqOrganizationOwner['status'] }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Badge tone={STATUS_TONE[status]} dot>
      {t(`organizations.ownerStatus.${status}`)}
    </Badge>
  );
}

export function OwnerInvitationPanel({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('hq');
  const owner = useOrganizationOwner(organizationId);
  const last = useLastInvitation(organizationId).data ?? null;

  let body: ReactNode;
  if (owner.data === undefined) {
    body = owner.isError ? (
      <LoadFailure error={owner.error} message={t('organizations.owner.unavailable')} onRetry={() => void owner.refetch()} />
    ) : (
      <Skeleton height="3.5rem" />
    );
  } else {
    body = <OwnerState organizationId={organizationId} owner={owner.data} last={last} />;
  }

  return (
    <section className="console-hq-panel" aria-labelledby="console-hq-owner" id="console-hq-owner-card">
      <h2 id="console-hq-owner" className="console-hq-owner__title">
        <span>{t('organizations.owner.title')}</span>
        {owner.data === undefined ? null : <OwnerStatusBadge status={owner.data.status} />}
        {owner.data?.status === 'joined' ? <InfoTip>{t('organizations.owner.hasOwner')}</InfoTip> : null}
      </h2>
      {body}
    </section>
  );
}

function OwnerState({
  organizationId,
  owner,
  last,
}: {
  readonly organizationId: string;
  readonly owner: HqOrganizationOwner;
  readonly last: CreatedInvitation | null;
}) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const when = (iso: string) => format.dateTime(iso, { date: 'medium', time: 'short' });
  const [inviting, setInviting] = useState(false);

  if (owner.status === 'joined' && owner.owner !== null) {
    const person = owner.owner;
    const contact = [person.email, person.phoneNumber].filter((value): value is string => value !== null).join(' · ');
    return (
      <div className="console-hq-owner">
        <PersonCard
          name={person.name ?? t('organizations.owner.title')}
          contact={contact === '' ? undefined : contact}
          meta={t('organizations.owner.joinedOn', { date: when(person.joinedAt) })}
        />
      </div>
    );
  }

  const invitation = owner.invitation;
  // The invitation this session just made, while it is still the newest one and its message did not go: its link, once.
  const justMade =
    last !== null && invitation !== null && last.id === invitation.id && invitation.status === 'pending' && last.delivery.status === 'not_sent' ? last : null;

  return (
    <div className="console-hq-owner">
      {invitation === null ? <p>{t('organizations.owner.none')}</p> : <InvitationSummary invitation={invitation} />}
      {justMade === null ? null : <InvitationOutcome invitation={justMade} />}
      <OwnerActions organizationId={organizationId} invitation={invitation} onInviteSomeoneElse={() => setInviting(true)} />
      <InviteOwnerDialog organizationId={organizationId} open={inviting} onClose={() => setInviting(false)} />
    </div>
  );
}

/** What became of the newest owner invitation, in a sentence or two. */
function InvitationSummary({ invitation }: { readonly invitation: HqOwnerInvitation }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const when = (iso: string) => format.dateTime(iso, { date: 'medium', time: 'short' });
  const by = t(`organizations.owner.by.${invitation.channel}`);
  const destination = invitation.destination;

  if (invitation.status === 'expired') {
    return <p>{t('organizations.owner.expired', { destination, date: when(invitation.expiresAt) })}</p>;
  }
  if (invitation.status === 'revoked') {
    return <p>{t('organizations.owner.withdrawn', { destination, date: when(invitation.revokedAt ?? invitation.invitedAt) })}</p>;
  }
  if (invitation.status === 'accepted') {
    return <p>{t('organizations.owner.acceptedNoOwner', { destination })}</p>;
  }
  const delivery = invitation.delivery;
  return (
    <>
      <p>
        {invitation.invitedByName === null
          ? t('organizations.owner.invitedOn', { date: when(invitation.invitedAt), by, destination })
          : t('organizations.owner.invitedOnBy', { date: when(invitation.invitedAt), name: invitation.invitedByName, by, destination })}
      </p>
      <p className="console-muted">
        {delivery.status === 'sent'
          ? t('organizations.owner.delivery.sent', { channel: t(`organizations.owner.channel.${delivery.channel ?? 'email'}`) })
          : t(`organizations.owner.delivery.${delivery.status}`)}{' '}
        {t('organizations.owner.validUntil', { date: when(invitation.expiresAt) })}
      </p>
    </>
  );
}

function OwnerActions({
  organizationId,
  invitation,
  onInviteSomeoneElse,
}: {
  readonly organizationId: string;
  readonly invitation: HqOwnerInvitation | null;
  readonly onInviteSomeoneElse: () => void;
}) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const resend = useResendOwnerInvitation(organizationId);
  const withdraw = useWithdrawOwnerInvitation(organizationId);
  const link = useOwnerInvitationLink(organizationId);
  const resendFeedback = useButtonFeedback(resend);
  const linkFeedback = useButtonFeedback(link);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const refused = (error: unknown): string => {
    const code = problemCode(error);
    if (code === PROBLEM.conflict) return t('organizations.owner.hasOwner');
    if (code === PROBLEM.invitationUnavailable) return t('organizations.owner.notPending');
    if (code === PROBLEM.alreadyAMember) return t('organizations.owner.alreadyMember');
    if (code === PROBLEM.forbidden) return t('organizations.owner.forbidden');
    if (code === PROBLEM.phoneInvitationsOff) return problems.text(error, { fallback: t('organizations.owner.phoneOff') });
    return problems.text(error, { fallback: t('organizations.owner.failed') });
  };

  const sendFresh = () => {
    if (invitation === null) return;
    setFailure(null);
    setCopied(null);
    resend.mutate(invitation.id, {
      onSuccess: (fresh) =>
        show({
          tone: 'ok',
          title: fresh.delivery.status === 'sent' ? t('organizations.owner.resent', { destination: fresh.destination }) : t('organizations.owner.resentNotSent'),
        }),
      onError: (error) => setFailure(refused(error)),
    });
  };

  const copyLink = () => {
    if (invitation === null) return;
    setFailure(null);
    link.mutate(invitation.id, {
      onSuccess: (result) => {
        setCopied(result.acceptUrl);
        void navigator.clipboard?.writeText(result.acceptUrl).then(
          () => show({ tone: 'ok', title: t('organizations.owner.linkCopied') }),
          () => undefined,
        );
      },
      onError: (error) => setFailure(refused(error)),
    });
  };

  const pending = invitation?.status === 'pending';
  // A phone invitation is not sent again while phone sign-in is off on the Platform (`auth.phoneSignIn`): the
  // server would refuse it. Withdraw it and invite them by email instead.
  const phoneOn = usePhoneSignIn();
  const phoneHeld = invitation !== null && invitation.channel === 'phone' && !phoneOn;
  const canResend = invitation !== null && invitation.status !== 'accepted' && !phoneHeld;

  return (
    <>
      {failure === null ? null : (
        <div className="console-alert" role="alert">
          <p>{failure}</p>
        </div>
      )}
      {phoneHeld && invitation?.status !== 'accepted' ? <p className="console-muted">{t('organizations.owner.phoneOffResend')}</p> : null}
      {copied === null ? null : (
        <div className="console-notice console-hq-invitation" role="status">
          <p>{t('organizations.owner.linkHint')}</p>
          <p>
            <span className="console-muted">{t('organizations.owner.link')}</span> <span className="console-code">{copied}</span>
          </p>
        </div>
      )}
      <div className="console-form__actions console-hq-owner__actions">
        {canResend ? (
          <Button variant={pending ? 'secondary' : 'primary'} {...resendFeedback} onClick={sendFresh}>
            {t('organizations.owner.resend')}
          </Button>
        ) : null}
        {pending ? (
          <Button variant="secondary" {...linkFeedback} onClick={copyLink}>
            {t('organizations.owner.copyLink')}
          </Button>
        ) : null}
        {pending ? (
          <Button variant="ghost" onClick={() => setConfirming(true)}>
            {t('organizations.owner.withdraw')}
          </Button>
        ) : (
          <Button variant={invitation === null ? 'primary' : 'ghost'} onClick={onInviteSomeoneElse}>
            {invitation === null ? t('organizations.owner.inviteOwner') : t('organizations.owner.inviteSomeoneElse')}
          </Button>
        )}
      </div>
      {invitation === null ? null : (
        <Dialog
          open={confirming}
          onOpenChange={setConfirming}
          title={t('organizations.owner.withdrawTitle')}
          description={t('organizations.owner.withdrawBody', { destination: invitation.destination })}
          footer={
            <>
              <Button variant="ghost" onClick={() => setConfirming(false)}>
                {t('organizations.owner.keep')}
              </Button>
              <Button
                variant="danger"
                busy={withdraw.isPending}
                onClick={() =>
                  withdraw.mutate(invitation.id, {
                    onSuccess: () => {
                      setConfirming(false);
                      setCopied(null);
                      show({ tone: 'ok', title: t('organizations.owner.withdrawnToast') });
                    },
                    onError: (error) => {
                      setConfirming(false);
                      setFailure(refused(error));
                    },
                  })
                }
              >
                {t('organizations.owner.withdrawConfirm')}
              </Button>
            </>
          }
        />
      )}
    </>
  );
}

type InviteFailure = { readonly kind: 'message'; readonly message: string; readonly details?: readonly string[] } | { readonly kind: 'error'; readonly error: unknown };

/** Inviting someone — the first owner, or someone other than the last invitation's contact — in a centred dialog. */
function InviteOwnerDialog({ organizationId, open, onClose }: { readonly organizationId: string; readonly open: boolean; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const invite = useInviteOwner(organizationId);
  const form = useForm<OwnerContactValues>({ defaultValues: EMPTY_OWNER });
  const channel = useWatch({ control: form.control, name: 'channel' });
  const [failure, setFailure] = useState<InviteFailure | undefined>(undefined);

  const close = () => {
    form.reset(EMPTY_OWNER);
    setFailure(undefined);
    onClose();
  };

  const submit = form.handleSubmit(async (values) => {
    setFailure(undefined);
    try {
      await invite.mutateAsync(ownerContactOf(values));
      close();
    } catch (error) {
      const code = problemCode(error);
      const field = values.channel === 'email' ? 'email' : 'phoneNumber';
      if (code === PROBLEM.conflict) {
        setFailure({ kind: 'message', message: t('organizations.owner.hasOwner') });
      } else if (code === PROBLEM.alreadyAMember) {
        form.setError(field, { type: 'server', message: t('organizations.owner.alreadyMember') });
      } else if (code === PROBLEM.validationFailed) {
        const details = placeFieldErrors(error, { email: 'email', phoneNumber: 'phoneNumber' }, (name, message) =>
          form.setError(name as keyof OwnerContactValues, { type: 'server', message }),
        );
        setFailure({ kind: 'message', message: t('organizations.new.refused'), details });
      } else if (code === PROBLEM.forbidden) {
        setFailure({ kind: 'message', message: t('organizations.owner.forbidden') });
      } else if (code === PROBLEM.phoneInvitationsOff) {
        setFailure({ kind: 'message', message: problems.text(error, { fallback: t('organizations.owner.phoneOff') }) });
      } else if (code === PROBLEM.notFound) {
        setFailure({ kind: 'message', message: t('organizations.page.noSuch') });
      } else {
        setFailure({ kind: 'error', error });
      }
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (next ? undefined : close())}
      size="md"
      title={t('organizations.owner.inviteTitle')}
      description={t('organizations.owner.inviteIntro')}
    >
      <form className="console-form" noValidate aria-label={t('organizations.owner.inviteTitle')} onSubmit={(event) => void submit(event)}>
        {failure === undefined ? null : failure.kind === 'error' ? (
          <LoadFailure error={failure.error} />
        ) : (
          <div className="console-alert" role="alert">
            <p>{failure.message}</p>
            {failure.details !== undefined && failure.details.length > 0 ? (
              <ul>
                {failure.details.map((detail) => (
                  <li key={detail}>{detail}</li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
        <OwnerContactFields control={form.control} names={{ channel: 'channel', email: 'email', phoneNumber: 'phoneNumber' }} channel={channel} />
        <div className="console-form__actions">
          <Button variant="ghost" onClick={close}>
            {t('organizations.new.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={form.formState.isSubmitting}>
            {t('organizations.owner.invite')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
